import type { RoomSearchFormProps } from './room-search-form.js';

import { useRef, useState } from 'react';

import { formatOccupancy } from '@elara/client-core/lib/occupancy-utils';
import { cn } from '@elara/client-core/lib/utils';

import { Button } from '#ui/button.js';
import { FloatingField } from '#ui/floating-field.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';
import { Popover, PopoverContent, PopoverTrigger } from '#ui/popover.js';

import { GuestPicker, StayCalendar } from './room-search-fields.js';
import { describeStay, FIELD_SURFACE, TWO_MONTHS_MIN_WIDTH, useChildAgeSlots } from './room-search-form.js';

/**
 * The hotel guide's room search form above the results (ELARA-1092), after Airbnb's search bar: one
 * raised field holding floating-label segments for the dates and the guests, each opening its picker in
 * a panel below it, and the search button. The field keeps its focus ring while a panel is open, though
 * focus has moved into it. On an empty tab the same form opens out instead (`RoomSearchPanels`).
 */
export function RoomSearchBar({
  startDate,
  endDate,
  occupancy,
  prefillKey,
  onDatesChange,
  onOccupancyChange,
  onSearch,
  canSearch,
  isSearching,
  disabled,
}: RoomSearchFormProps) {
  const barRef = useRef<HTMLDivElement>(null);
  const [datesOpen, setDatesOpen] = useState(false);
  const [monthCount, setMonthCount] = useState<1 | 2>(1);
  const slots = useChildAgeSlots(occupancy, prefillKey, onOccupancyChange);

  function handleDatesOpenChange(open: boolean) {
    // The guide's width, not the bar's: the bar fits its values, the panel may run past it.
    if (open) setMonthCount((barRef.current?.parentElement?.offsetWidth ?? 0) >= TWO_MONTHS_MIN_WIDTH ? 2 : 1);
    setDatesOpen(open);
  }

  const stay = describeStay(startDate, endDate);
  const guests = formatOccupancy({ adults: occupancy.adults, children: occupancy.children });

  return (
    <div
      ref={barRef}
      className={cn(
        // Fits its values once side by side; stacked in a narrow guide, it takes the width. `isolate`
        // keeps the floating labels' `z-10` inside the bar, so they pass under the guide's floating
        // header rather than painting over it.
        'isolate flex w-full max-w-full flex-col @xl:w-fit @xl:flex-row @xl:items-center',
        FIELD_SURFACE,
      )}
    >
      {/* The segments, ruled apart: side by side in a wide guide, stacked in a narrow one. */}
      <div className="flex min-w-0 flex-1 flex-col divide-y divide-border @xl:flex-none @xl:flex-row @xl:divide-y-0">
        <BarField label="When" value={stay} disabled={disabled}>
          {(trigger) => (
            <Popover open={datesOpen} onOpenChange={handleDatesOpenChange}>
              <PopoverTrigger asChild>{trigger}</PopoverTrigger>
              <PopoverContent align="start" className="w-auto p-3">
                <StayCalendar
                  // Opens on the stay's month; remounted by a fresh pre-fill landing while it is open.
                  key={prefillKey}
                  startDate={startDate}
                  endDate={endDate}
                  onDatesChange={onDatesChange}
                  onStayComplete={() => setDatesOpen(false)}
                  monthCount={monthCount}
                />
              </PopoverContent>
            </Popover>
          )}
        </BarField>
        {/* Side by side, the rule between the segments is inset from the bar's top and bottom edges,
            a little further than the search button is (`m-1`), so it reads as a divider within the bar. */}
        <span aria-hidden className="my-2 hidden w-px shrink-0 self-stretch bg-border @xl:block" />
        <BarField label="Who" value={guests} disabled={disabled}>
          {(trigger) => (
            <Popover>
              <PopoverTrigger asChild>{trigger}</PopoverTrigger>
              <PopoverContent align="start" className="w-80">
                <GuestPicker occupancy={occupancy} slots={slots} />
              </PopoverContent>
            </Popover>
          )}
        </BarField>
      </div>
      {/* One button for both shapes. Stacked in a narrow guide it runs the bar's width, "Find rooms" in
          words, as on the open form; side by side it is the square search glyph at the bar's end, its
          words kept for assistive tech (`sr-only`), so its name is the same at any width. */}
      <Button
        onClick={onSearch}
        disabled={!canSearch}
        isBusy={isSearching}
        className="m-2 @xl:m-1 @xl:size-10 @xl:shrink-0 @xl:p-0"
      >
        {/* Shown and hidden on a wrapper: `MaterialIcon` sets its own `display` inline, which beats a class. */}
        <span className="hidden @xl:inline-flex">
          <MaterialIcon icon="search" size={IconSizes.sm} />
        </span>
        <span className="@xl:sr-only">Find rooms</span>
      </Button>
    </div>
  );
}

/**
 * A floating-label segment whose value is a button opening a panel (`children` wraps it in its popover).
 * The label rests as the placeholder until there is a value. Borderless: the bar draws the surface.
 */
function BarField({
  label,
  value,
  disabled,
  children,
}: {
  label: string;
  value: string | undefined;
  disabled?: boolean;
  children: (trigger: React.ReactElement) => React.ReactNode;
}) {
  return (
    // A floor, so an empty or short value still reads as a field rather than a label.
    <div className="min-w-0 @xl:min-w-44">
      <FloatingField
        label={label}
        filled={!!value}
        disabled={disabled}
        className="rounded-none border-0 bg-transparent"
      >
        {children(
          <button
            type="button"
            disabled={disabled}
            aria-label={value ? `${label}: ${value}` : label}
            className="flex h-12 w-full min-w-0 cursor-pointer items-center px-3 pt-5 pb-1 text-left text-base focus:outline-none disabled:cursor-not-allowed"
          >
            <span className="truncate">{value}</span>
          </button>,
        )}
      </FloatingField>
    </div>
  );
}
